import { FC, useEffect, useRef, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExpand, faFilter, faFilterCircleXmark, faSort, faSortUp, faSortDown } from "@fortawesome/free-solid-svg-icons";
import ModalDefault from "../ModalDefault/ModalDefault";
import { isColumnNumeric, compareTexts } from "../MarkdownMessage/tableUtils";
import "./HtmlMessage.scss";

interface HtmlMessageProps {
  content: string;
}

const FULLSCREEN_ICON_HTML = renderToStaticMarkup(<FontAwesomeIcon icon={faExpand} />);
const SORT_ICON_HTML = renderToStaticMarkup(<FontAwesomeIcon icon={faSort} className="markdown-table-sort-icon" />);
const SORT_ASC_ICON_HTML = renderToStaticMarkup(<FontAwesomeIcon icon={faSortUp} className="markdown-table-sort-icon" />);
const SORT_DESC_ICON_HTML = renderToStaticMarkup(<FontAwesomeIcon icon={faSortDown} className="markdown-table-sort-icon" />);
const FILTER_ICON_HTML = renderToStaticMarkup(<FontAwesomeIcon icon={faFilter} />);
const FILTER_ACTIVE_ICON_HTML = renderToStaticMarkup(<FontAwesomeIcon icon={faFilterCircleXmark} />);

type SortState = { columnIndex: number; direction: "asc" | "desc" } | null;

// Tabelas com rowspan/colspan (ex: nome do banco agrupando várias linhas) quebram
// visualmente ao esconder/reordenar linhas no sort/filtro, pois a célula com span some
// da linha e as demais deslocam de coluna. Expande cada span em células reais por linha.
function expandRowSpans(tbody: HTMLTableSectionElement, numCols: number) {
  const rows = Array.from(tbody.querySelectorAll(":scope > tr")) as HTMLTableRowElement[];
  const carryOver: ({ html: string; remaining: number } | undefined)[] = new Array(numCols);

  rows.forEach((row) => {
    const originalCells = Array.from(row.children) as HTMLTableCellElement[];
    let origIdx = 0;
    const newRowHtml: string[] = [];

    for (let col = 0; col < numCols; col++) {
      const carry = carryOver[col];
      if (carry && carry.remaining > 0) {
        newRowHtml.push(carry.html);
        carry.remaining -= 1;
        if (carry.remaining === 0) carryOver[col] = undefined;
        continue;
      }

      const cell = originalCells[origIdx++];
      if (!cell) {
        newRowHtml.push("<td></td>");
        continue;
      }

      const rowSpan = parseInt(cell.getAttribute("rowspan") || "1", 10);
      const colSpan = parseInt(cell.getAttribute("colspan") || "1", 10);
      const cellHtml = `<td>${cell.innerHTML}</td>`;
      newRowHtml.push(cellHtml);
      if (rowSpan > 1) carryOver[col] = { html: cellHtml, remaining: rowSpan - 1 };

      for (let extra = 1; extra < colSpan && col + 1 < numCols; extra++) {
        col += 1;
        newRowHtml.push(cellHtml);
        if (rowSpan > 1) carryOver[col] = { html: cellHtml, remaining: rowSpan - 1 };
      }
    }

    row.innerHTML = newRowHtml.join("");
  });
}

// Reaproveita as mesmas classes CSS do sort/filter de MarkdownMessage (globais, fora do
// escopo `.MarkdownMessage` justamente para valer também em portais como este) para manter
// visual idêntico entre tabelas vindas de markdown e tabelas em HTML puro.
function enhanceTableSortFilter(table: HTMLTableElement) {
  if (table.dataset.sortFilterEnhanced === "true") return;

  const thead = table.querySelector("thead");
  const tbody = table.querySelector("tbody");
  const headerRow = thead?.querySelector(":scope > tr");
  if (!thead || !tbody || !headerRow) return;

  const ths = Array.from(headerRow.querySelectorAll(":scope > th"));
  if (ths.length === 0) return;

  table.dataset.sortFilterEnhanced = "true";

  expandRowSpans(tbody, ths.length);

  const bodyRows = Array.from(tbody.querySelectorAll(":scope > tr"));
  const cellText = (row: Element, colIndex: number) =>
    row.children[colIndex]?.textContent?.trim() ?? "";

  const columnNumeric = ths.map((_, colIndex) =>
    isColumnNumeric(bodyRows.map((row) => cellText(row, colIndex)))
  );
  const columnValues = ths.map((_, colIndex) =>
    Array.from(new Set(bodyRows.map((row) => cellText(row, colIndex)))).sort((a, b) =>
      a.localeCompare(b, "pt-BR", { sensitivity: "base" })
    )
  );

  let sortState: SortState = null;
  const activeFilters = new Map<number, Set<string>>();
  const sortIconEls: HTMLElement[] = [];
  const filterBtnEls: HTMLButtonElement[] = [];
  let closeActivePopup: (() => void) | null = null;

  const applyState = () => {
    let visibleRows = bodyRows.slice();

    if (activeFilters.size > 0) {
      visibleRows = visibleRows.filter((row) =>
        Array.from(activeFilters.entries()).every(([colIdx, allowed]) => allowed.has(cellText(row, colIdx)))
      );
    }

    if (sortState) {
      const { columnIndex, direction } = sortState;
      const numeric = columnNumeric[columnIndex];
      visibleRows.sort((a, b) => compareTexts(cellText(a, columnIndex), cellText(b, columnIndex), numeric, direction));
    }

    const visibleSet = new Set(visibleRows);
    visibleRows.forEach((row) => tbody.appendChild(row));
    bodyRows.forEach((row) => {
      (row as HTMLElement).style.display = visibleSet.has(row) ? "" : "none";
    });
  };

  const renderSortIcons = () => {
    sortIconEls.forEach((icon, colIndex) => {
      const html =
        sortState?.columnIndex === colIndex
          ? sortState.direction === "asc"
            ? SORT_ASC_ICON_HTML
            : SORT_DESC_ICON_HTML
          : SORT_ICON_HTML;
      const wrap = document.createElement("span");
      wrap.innerHTML = html;
      const next = wrap.firstElementChild;
      if (next) icon.replaceWith(next);
      sortIconEls[colIndex] = next as HTMLElement;
    });
  };

  const renderFilterButtons = () => {
    filterBtnEls.forEach((btn, colIndex) => {
      const isActive = activeFilters.has(colIndex);
      btn.classList.toggle("active", isActive);
      const wrap = document.createElement("span");
      wrap.innerHTML = isActive ? FILTER_ACTIVE_ICON_HTML : FILTER_ICON_HTML;
      const svg = btn.querySelector("svg");
      const next = wrap.firstElementChild;
      if (svg && next) svg.replaceWith(next);
    });
  };

  const openFilterPopup = (colIndex: number, anchorBtn: HTMLButtonElement) => {
    closeActivePopup?.();

    const values = columnValues[colIndex];
    const draft = new Set<string>(activeFilters.get(colIndex) ?? values);

    const popup = document.createElement("div");
    popup.className = "markdown-table-filter-popup";
    const anchorRect = anchorBtn.getBoundingClientRect();
    popup.style.position = "fixed";
    popup.style.top = `${anchorRect.bottom + 4}px`;
    popup.style.left = `${Math.min(anchorRect.left, window.innerWidth - 260)}px`;

    const searchInput = document.createElement("input");
    searchInput.type = "text";
    searchInput.className = "markdown-table-filter-search";
    searchInput.placeholder = "Buscar...";

    const actions = document.createElement("div");
    actions.className = "markdown-table-filter-actions";
    const selectAllBtn = document.createElement("button");
    selectAllBtn.type = "button";
    selectAllBtn.textContent = "Selecionar todos";
    const clearBtn = document.createElement("button");
    clearBtn.type = "button";
    clearBtn.textContent = "Limpar";
    actions.append(selectAllBtn, clearBtn);

    const list = document.createElement("div");
    list.className = "markdown-table-filter-list";

    const renderList = () => {
      list.innerHTML = "";
      const search = searchInput.value.toLowerCase();
      const filtered = values.filter((v) => v.toLowerCase().includes(search));

      if (filtered.length === 0) {
        const empty = document.createElement("div");
        empty.className = "markdown-table-filter-empty";
        empty.textContent = "Nenhum valor encontrado";
        list.appendChild(empty);
        return;
      }

      filtered.forEach((value) => {
        const label = document.createElement("label");
        label.className = "markdown-table-filter-item";
        const checkbox = document.createElement("input");
        checkbox.type = "checkbox";
        checkbox.checked = draft.has(value);
        checkbox.addEventListener("change", () => {
          if (checkbox.checked) draft.add(value);
          else draft.delete(value);
        });
        const span = document.createElement("span");
        span.textContent = value || "(vazio)";
        label.append(checkbox, span);
        list.appendChild(label);
      });
    };

    searchInput.addEventListener("input", renderList);
    selectAllBtn.addEventListener("click", () => {
      values.forEach((v) => draft.add(v));
      renderList();
    });
    clearBtn.addEventListener("click", () => {
      draft.clear();
      renderList();
    });

    const footer = document.createElement("div");
    footer.className = "markdown-table-filter-footer";
    const cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.className = "btn-cancel";
    cancelBtn.textContent = "Cancelar";
    const applyBtn = document.createElement("button");
    applyBtn.type = "button";
    applyBtn.className = "btn-apply";
    applyBtn.textContent = "Aplicar";
    footer.append(cancelBtn, applyBtn);

    cancelBtn.addEventListener("click", () => closeActivePopup?.());
    applyBtn.addEventListener("click", () => {
      if (draft.size === values.length) activeFilters.delete(colIndex);
      else activeFilters.set(colIndex, new Set(draft));
      renderFilterButtons();
      applyState();
      closeActivePopup?.();
    });

    popup.append(searchInput, actions, list, footer);
    renderList();

    const modalAncestor = anchorBtn.closest(".modal") as HTMLElement | null;
    const portalContainer = modalAncestor ?? document.body;
    portalContainer.appendChild(popup);
    searchInput.focus();

    const handleOutside = (e: MouseEvent) => {
      if (!popup.contains(e.target as Node)) closeActivePopup?.();
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeActivePopup?.();
    };
    const handleScrollOrResize = (e: Event) => {
      if (e.target instanceof Node && popup.contains(e.target)) return;
      closeActivePopup?.();
    };

    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleKey);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);

    closeActivePopup = () => {
      popup.remove();
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleKey);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
      closeActivePopup = null;
    };
  };

  ths.forEach((th, colIndex) => {
    th.classList.add("align-middle", "markdown-table-th");

    const headerContent = document.createElement("div");
    headerContent.className = "markdown-table-th-content";

    const labelSpan = document.createElement("span");
    while (th.firstChild) labelSpan.appendChild(th.firstChild);
    const headerText = labelSpan.textContent?.trim() ?? "";

    const sortBtn = document.createElement("button");
    sortBtn.type = "button";
    sortBtn.className = "markdown-table-sort-btn";
    sortBtn.setAttribute("aria-label", `Ordenar por ${headerText}`);

    const sortIconWrap = document.createElement("span");
    sortIconWrap.innerHTML = SORT_ICON_HTML;
    const sortIconEl = sortIconWrap.firstElementChild as HTMLElement;
    sortIconEls[colIndex] = sortIconEl;

    sortBtn.appendChild(labelSpan);
    sortBtn.appendChild(sortIconEl);
    sortBtn.addEventListener("click", () => {
      if (!sortState || sortState.columnIndex !== colIndex) sortState = { columnIndex: colIndex, direction: "asc" };
      else if (sortState.direction === "asc") sortState = { columnIndex: colIndex, direction: "desc" };
      else sortState = null;
      renderSortIcons();
      applyState();
    });

    const filterBtn = document.createElement("button");
    filterBtn.type = "button";
    filterBtn.className = "markdown-table-filter-btn";
    filterBtn.setAttribute("aria-label", `Filtrar ${headerText}`);
    const filterIconWrap = document.createElement("span");
    filterIconWrap.innerHTML = FILTER_ICON_HTML;
    filterBtn.appendChild(filterIconWrap.firstElementChild as Node);
    filterBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      openFilterPopup(colIndex, filterBtn);
    });
    filterBtnEls[colIndex] = filterBtn;

    headerContent.appendChild(sortBtn);
    headerContent.appendChild(filterBtn);
    th.appendChild(headerContent);
  });
}

const HtmlMessage: FC<HtmlMessageProps> = ({ content }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const fullscreenContainerRef = useRef<HTMLDivElement>(null);
  const [fullscreenTableHtml, setFullscreenTableHtml] = useState<string | null>(null);

  // Religa sort/filtro na tabela clonada pro modal "Tela cheia" (outerHTML não copia listeners).
  useEffect(() => {
    const container = fullscreenContainerRef.current;
    if (!fullscreenTableHtml || !container) return;

    const table = container.querySelector("table") as HTMLTableElement | null;
    if (!table) return;

    delete table.dataset.sortFilterEnhanced;
    enhanceTableSortFilter(table);
  }, [fullscreenTableHtml]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const tables = Array.from(container.querySelectorAll("table"));

    tables.forEach((table) => {
      if (table.closest(".html-table-block")) return;

      table.classList.add("table", "table-striped");
      // HTML cru, capturado antes de enhanceTableSortFilter, pra clonar no modal sem duplicar botões.
      const rawTableHtml = table.outerHTML;
      enhanceTableSortFilter(table);

      const block = document.createElement("div");
      block.className = "html-table-block";

      const button = document.createElement("button");
      button.type = "button";
      button.className = "html-table-fullscreen-btn";
      button.setAttribute("aria-label", "Visualizar tabela em tela cheia");
      button.innerHTML = `${FULLSCREEN_ICON_HTML}<span>Tela cheia</span>`;
      button.addEventListener("click", () => setFullscreenTableHtml(rawTableHtml));

      const responsive = document.createElement("div");
      responsive.className = "table-responsive";

      table.parentNode?.insertBefore(block, table);
      responsive.appendChild(table);
      block.appendChild(button);
      block.appendChild(responsive);
    });
  });

  return (
    <>
      <div
        ref={containerRef}
        className="HtmlMessage"
        data-testid="HtmlMessage"
        dangerouslySetInnerHTML={{ __html: content }}
      />

      <ModalDefault
        title="Visualização da tabela"
        show={!!fullscreenTableHtml}
        onClose={() => setFullscreenTableHtml(null)}
        sizeModal="xxl"
        showFooter={false}
        scrollable
        className="html-table-modal"
      >
        <div
          ref={fullscreenContainerRef}
          className="table-responsive"
          dangerouslySetInnerHTML={{ __html: fullscreenTableHtml ?? "" }}
        />
      </ModalDefault>
    </>
  );
};

export default HtmlMessage;
